import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { API } from 'core/API';

import {
	IPlaceholderRequest,
	PaginationRequest,
	PlaceholderRequest,
	PlaceholderResponse,
	SearchPlaceholdersRequest,
	SearchPlaceholdersResponse,
	SearchPlaceholdersSort,
	UpdatePlaceholderRequest,
} from '@joc/api-gateway';

import { ADMIN_PLACEHOLDERS_QUERY_KEY } from 'core/constants';
import { useState } from 'react';
import { PlaceholderUpdatePayload } from 'components/Organization/Placeholders/placeholderMappers';

const fetchPlaceholdersByAdminAPI = ({
	skip,
	take,

	search,
	sort,
}: {
	skip: number;
	take: number;

	search?: string;
	sort?: SearchPlaceholdersSort;
}) =>
	API.searchPlaceholdersByAdmin(
		undefined,
		SearchPlaceholdersRequest.fromJS({
			pagination: PaginationRequest.fromJS({ skip, take }),
			search,
			sort,
		})
	);

const createPlaceholderAPI = (requestBody: IPlaceholderRequest) =>
	API.createPlaceholder(undefined, PlaceholderRequest.fromJS(requestBody));

const editPlaceholderAPI = (id: number, requestBody: PlaceholderUpdatePayload) =>
	API.updatePlaceholder(id, undefined, requestBody as unknown as UpdatePlaceholderRequest);

const deletePlaceholderAPI = (id: number) => API.deletePlaceholder(id, undefined);

export const usePlaceholdersByAdmin = (search?: string, sort?: SearchPlaceholdersSort) =>
	useInfiniteQuery<SearchPlaceholdersResponse, unknown>({
		queryKey: [ADMIN_PLACEHOLDERS_QUERY_KEY, { search, sort }],
		initialPageParam: 0,
		retry: false,
		queryFn: ({ pageParam = 0 }) =>
			fetchPlaceholdersByAdminAPI({ skip: pageParam as number, take: 10, search, sort }),
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.records.length < 10 ? undefined : allPages.length * 10;
		},
		refetchOnWindowFocus: false,
	});

export const useCreatePlaceholder = () => {
	const queryClient = useQueryClient();
	const {
		mutate: createPlaceholder,
		data,
		isPending,
		isSuccess,
	} = useMutation<PlaceholderResponse, Error, { body: IPlaceholderRequest }>({
		mutationFn: ({ body }) => createPlaceholderAPI(body),
		onSettled: () => queryClient.invalidateQueries({ queryKey: [ADMIN_PLACEHOLDERS_QUERY_KEY] }),
	});

	return { createPlaceholder, data, isPending, isSuccess };
};

export const useEditPlaceholder = () => {
	const queryClient = useQueryClient();
	const {
		mutate: editPlaceholder,
		data,
		isPending,
		isSuccess,
	} = useMutation<PlaceholderResponse, Error, { id: number; body: PlaceholderUpdatePayload }>({
		mutationFn: ({ id, body }) => editPlaceholderAPI(id, body),
		onSettled: () => queryClient.invalidateQueries({ queryKey: [ADMIN_PLACEHOLDERS_QUERY_KEY] }),
	});

	return { editPlaceholder, data, isPending, isSuccess };
};

export const useDeletePlaceholder = () => {
	const queryClient = useQueryClient();
	const {
		mutate: deletePlaceholder,
		data,
		isPending,
		isSuccess,
	} = useMutation<void, Error, { id: number }>({
		mutationFn: ({ id }) => deletePlaceholderAPI(id),
		onSettled: () => queryClient.invalidateQueries({ queryKey: [ADMIN_PLACEHOLDERS_QUERY_KEY] }),
	});

	return { deletePlaceholder, data, isPending, isSuccess };
};

export const useCachedTotalPlaceholders = () => {
	const queryClient = useQueryClient();
	const [totalItems, setTotalItems] = useState(0);

	queryClient.getQueryCache().subscribe(cache => {
		if (cache?.query?.queryKey?.includes(ADMIN_PLACEHOLDERS_QUERY_KEY))
			setTimeout(() => setTotalItems(cache?.query?.state?.data?.pages?.[0]?.total ?? 0), 300);
	});

	return totalItems;
};
